import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExternalLink,
  Edit3,
  MessageCircle,
  PartyPopper,
  FileText,
  Layout,
  AlertTriangle,
} from 'lucide-react';
import roaryMascot from '/assets/Roary-mascot.png';

interface CompletionData {
  siteId: string;
  siteName: string;
  pagesCreated: number;
  componentsCreated: number;
  previewUrl?: string;
  homePageId?: string;
  // Populated by the agentic build pipeline. Undefined for older flows.
  pagesVerified?: number;
  pagesFailed?: number;
  failedPages?: Array<{ slug: string; title: string; issues: string[] }>;
  layoutsVerified?: number;
  layoutsFailed?: number;
  failedLayouts?: Array<{ name: string; issues: string[] }>;
  stopReason?: string;
  // Page-mode fields — set when Roary built a single page inside a site.
  mode?: 'site' | 'page';
  pageId?: string;
  pageTitle?: string;
  pageSlug?: string;
}

interface RoaryCompleteProps {
  completionData: CompletionData;
  onContinue: () => void;
  onClose: () => void;
}

// Confetti particle component
const Confetti: React.FC = () => {
  const colors = ['#f97316', '#8b5cf6', '#eab308', '#22c55e', '#3b82f6'];
  const particles = [...Array(50)].map((_, i) => ({
    id: i,
    color: colors[i % colors.length],
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    size: 4 + Math.random() * 6,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-sm"
          style={{
            left: `${p.left}%`,
            top: '-20px',
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.color,
            animation: `roary-confetti ${p.duration}s ease-out ${p.delay}s forwards`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
};

// Stat card component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: number;
  label: string;
}> = ({ icon, value, label }) => (
  <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/50">
    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-700/50 flex items-center justify-center text-orange-400">
      {icon}
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-sm text-gray-400">{label}</div>
  </div>
);

export const RoaryComplete: React.FC<RoaryCompleteProps> = ({
  completionData,
  onContinue,
  onClose,
}) => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);

  const hasIssues =
    (completionData.pagesFailed ?? 0) > 0 || (completionData.layoutsFailed ?? 0) > 0;

  // Hide confetti after animation. A build with problems skips the celebration.
  useEffect(() => {
    if (hasIssues) {
      setShowConfetti(false);
      return;
    }
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, [hasIssues]);

  const isPageMode = completionData.mode === 'page' && !!completionData.pageId;

  // Navigate to site editor (or directly to the new page editor in page mode)
  const handleEditSite = () => {
    onClose();
    if (isPageMode) {
      navigate(`/sites/${completionData.siteId}/pages/${completionData.pageId}/editor`);
    } else {
      navigate(`/sites/${completionData.siteId}`);
    }
  };

  // Navigate to the same preview the editor uses.
  const handlePreview = () => {
    const pageId = isPageMode ? completionData.pageId : completionData.homePageId;
    if (pageId) {
      window.open(`/preview/${completionData.siteId}/${pageId}`, '_blank');
      return;
    }
    navigate(`/sites/${completionData.siteId}`);
  };

  return (
    <div className="h-full flex flex-col items-center justify-start p-8 pt-12 relative overflow-y-auto">
      {/* Confetti animation */}
      {showConfetti && !hasIssues && <Confetti />}

      {/* Celebration header */}
      <div className="text-center mb-8 relative z-10">
        <div className="flex items-center justify-center gap-3 mb-4">
          <PartyPopper className="w-8 h-8 text-yellow-400 animate-bounce" />
          <img src={roaryMascot} alt="Roary" className="w-20 h-20 object-contain drop-shadow-lg" />
          <PartyPopper
            className="w-8 h-8 text-yellow-400 animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">
          {isPageMode ? 'Roary added your page!' : 'Roary finished your website!'}
        </h2>
        <p className="text-gray-400">
          {isPageMode
            ? 'Your new page is ready to preview and edit'
            : 'Your website is ready to preview and customize'}
        </p>
      </div>

      {/* Preview card */}
      <div className="w-full max-w-2xl bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-6 relative z-10">
        {/* Preview iframe — the new page in page mode, the home page otherwise */}
        <div className="aspect-video bg-slate-900 rounded-xl mb-4 border border-slate-700/50 overflow-hidden">
          {(isPageMode ? completionData.pageId : completionData.homePageId) ? (
            <iframe
              src={`/preview/${completionData.siteId}/${
                isPageMode ? completionData.pageId : completionData.homePageId
              }`}
              title={isPageMode ? 'Page Preview' : 'Website Preview'}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <p>Preview loading...</p>
            </div>
          )}
        </div>

        {/* Title — page title in page mode, site name otherwise */}
        <h3 className="text-xl font-semibold text-white text-center mb-1">
          {isPageMode ? completionData.pageTitle || 'New page' : completionData.siteName}
        </h3>
        {isPageMode && (
          <p className="text-sm text-gray-400 text-center mb-4">
            in {completionData.siteName}
            {completionData.pageSlug ? ` · /${completionData.pageSlug}` : ''}
          </p>
        )}

        {/* Stats — hide in page mode (the single new page is the headline) */}
        {!isPageMode && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            <StatCard
              icon={<FileText className="w-5 h-5" />}
              value={completionData.pagesCreated}
              label={completionData.pagesCreated === 1 ? 'Page' : 'Pages'}
            />
            <StatCard
              icon={<Layout className="w-5 h-5" />}
              value={completionData.componentsCreated}
              label={completionData.componentsCreated === 1 ? 'Component' : 'Components'}
            />
          </div>
        )}

        {/* Verification warning — shown when the agentic pipeline reports
            any page OR layout that failed verification. */}
        {((completionData.pagesFailed ?? 0) > 0 || (completionData.layoutsFailed ?? 0) > 0) && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-amber-200 font-semibold mb-1">
                  Some sections may need another pass
                </div>
                <p className="text-sm text-amber-100/80 mb-2">
                  Roary couldn't fully verify these parts of your site. Ask Roary to review or
                  retry:
                </p>
                {completionData.failedLayouts && completionData.failedLayouts.length > 0 && (
                  <ul className="text-sm text-amber-100 space-y-1 mb-2">
                    {completionData.failedLayouts.map(l => (
                      <li key={l.name} className="pl-2">
                        • <span className="font-medium">{l.name}</span>{' '}
                        <span className="text-amber-200/70">(navbar / footer)</span>
                      </li>
                    ))}
                  </ul>
                )}
                {completionData.failedPages && completionData.failedPages.length > 0 && (
                  <ul className="text-sm text-amber-100 space-y-1">
                    {completionData.failedPages.map(p => (
                      <li key={p.slug} className="pl-2">
                        • <span className="font-medium">{p.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handlePreview}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            View it
          </button>
          <button
            onClick={handleEditSite}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white rounded-xl transition-colors shadow-lg shadow-orange-500/20"
          >
            <Edit3 className="w-5 h-5" />
            {isPageMode ? 'Edit the page' : 'Edit the site'}
          </button>
        </div>
      </div>

      {/* Continue conversation */}
      <div className="w-full max-w-2xl text-center relative z-10">
        <button
          onClick={onContinue}
          className="inline-flex items-center gap-2 px-6 py-3 text-gray-400 hover:text-white transition-colors"
        >
          <MessageCircle className="w-5 h-5" />
          Ask Roary for more changes
        </button>

        <div className="mt-4 text-sm text-gray-500 flex items-center justify-center gap-2">
          <span className="text-orange-400">💬</span>
          <span>&ldquo;Add a blog section&rdquo; or &ldquo;Change the color scheme&rdquo;</span>
        </div>
      </div>
    </div>
  );
};
